/**
 * Settings → Templates — Phase 3 message template library (per user).
 *
 * - List view by channel
 * - Inline create / edit / archive / delete
 * - Promo-code field rendered separately so it can be A/B-tested without
 *   editing the body (it gets injected via the {{promo_code}} variable)
 * - "Restore defaults" button to re-seed the starter pack
 *
 * Variables supported: {{realtor.first_name}}, {{realtor.last_name}},
 * {{realtor.full_name}}, {{realtor.brokerage_name}}, {{user.first_name}},
 * {{user.last_name}}, {{user.full_name}}, {{user.location}}, {{promo_code}}
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Facebook, Instagram, Linkedin, Twitter, Youtube, Mail, Phone,
  Plus, Trash2, Pencil, Save, X, RefreshCw, Tag, Variable,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";

const CHANNELS = [
  { key: "ig", label: "Instagram", Icon: Instagram, color: "text-[#E1306C]" },
  { key: "fb", label: "Facebook", Icon: Facebook, color: "text-[#1877F2]" },
  { key: "li", label: "LinkedIn", Icon: Linkedin, color: "text-[#0A66C2]" },
  { key: "x", label: "X / Twitter", Icon: Twitter, color: "text-slate-900" },
  { key: "tt", label: "TikTok", Icon: Instagram, color: "text-slate-900" }, // re-using IG icon
  { key: "yt", label: "YouTube", Icon: Youtube, color: "text-[#FF0000]" },
  { key: "email", label: "Email", Icon: Mail, color: "text-slate-700" },
  { key: "sms", label: "SMS", Icon: Phone, color: "text-emerald-600" },
];
const CHANNEL_BY_KEY = Object.fromEntries(CHANNELS.map((c) => [c.key, c]));

const VARIABLES = [
  { key: "{{realtor.first_name}}", hint: "Realtor first name" },
  { key: "{{realtor.last_name}}", hint: "Realtor last name" },
  { key: "{{realtor.full_name}}", hint: "Realtor full name" },
  { key: "{{realtor.brokerage_name}}", hint: "Realtor's brokerage" },
  { key: "{{user.first_name}}", hint: "Your first name" },
  { key: "{{user.full_name}}", hint: "Your full name" },
  { key: "{{user.location}}", hint: "Your city" },
  { key: "{{promo_code}}", hint: "Promo from this template" },
];

export default function Templates() {
  const [templates, setTemplates] = useState(null); // null = loading
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // template object or "new"
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/templates");
      setTemplates(data || []);
    } catch (e) {
      toast.error("Couldn't load templates", { description: formatApiError(e) });
      setTemplates([]);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!templates) return [];
    if (filter === "all") return templates;
    return templates.filter((t) => t.channel === filter);
  }, [templates, filter]);

  const restoreDefaults = async () => {
    try {
      const { data } = await api.post("/templates/seed-defaults", null, {
        params: { force: false },
      });
      toast.success(`Restored ${data.inserted} default templates`);
      load();
    } catch (e) {
      toast.error("Failed to restore", { description: formatApiError(e) });
    }
  };

  const removeTemplate = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/templates/${confirmDelete.id}`);
      toast.success(`Deleted "${confirmDelete.name}"`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  return (
    <div className="space-y-5" data-testid="templates-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Phase 3 · Social outreach
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900 mt-0.5">
            Message templates
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Per-channel message templates with variable substitution. Pick a template from a
            realtor card and we&apos;ll copy the rendered message to your clipboard before opening
            the native app. <strong>Promo codes</strong> live on the template so you can rotate
            offers without rewriting the body.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={restoreDefaults}
            className="min-h-[44px]"
            data-testid="templates-restore-defaults"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Restore defaults
          </Button>
          <Button
            onClick={() => setEditing("new")}
            className="min-h-[44px]"
            data-testid="templates-new-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            New template
          </Button>
        </div>
      </div>

      {/* Channel filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <ChipButton active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
          All ({templates?.length || 0})
        </ChipButton>
        {CHANNELS.map((c) => {
          const n = (templates || []).filter((t) => t.channel === c.key).length;
          if (n === 0) return null;
          const Icon = c.Icon;
          return (
            <ChipButton
              key={c.key}
              active={filter === c.key}
              onClick={() => setFilter(c.key)}
              testid={`filter-${c.key}`}
            >
              <Icon className={`h-3 w-3 mr-1 ${c.color}`} />
              {c.label} ({n})
            </ChipButton>
          );
        })}
      </div>

      {/* List */}
      {templates === null ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center">
          <Variable className="h-8 w-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600">
            No templates yet. Click <strong>New template</strong> or restore the starter pack.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onEdit={() => setEditing(t)}
              onDelete={() => setConfirmDelete(t)}
            />
          ))}
        </div>
      )}

      {editing ? (
        <TemplateEditor
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      ) : null}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={removeTemplate}
        title={`Delete "${confirmDelete?.name}"?`}
        description="This template will be permanently removed. You can restore the starter pack later."
        confirmLabel="Delete template"
        destructive
        testid="template-delete-confirm"
      />
    </div>
  );
}

function ChipButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors inline-flex items-center ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function TemplateCard({ template, onEdit, onDelete }) {
  const c = CHANNEL_BY_KEY[template.channel] || CHANNELS[0];
  const Icon = c.Icon;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:items-start"
      data-testid={`template-card-${template.id}`}
    >
      <div className={`shrink-0 h-8 w-8 rounded-md flex items-center justify-center bg-slate-50 border border-slate-200 ${c.color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-900">{template.name}</h3>
          <Badge variant="outline" className="text-[10px] text-slate-600">{c.label}</Badge>
          {template.is_default ? (
            <Badge variant="outline" className="text-[10px] bg-slate-50">Starter</Badge>
          ) : null}
          {template.promo_code ? (
            <Badge variant="outline" className="text-[10px] gap-1 bg-amber-50 border-amber-200 text-amber-800">
              <Tag className="h-3 w-3" />
              {template.promo_code}
            </Badge>
          ) : null}
        </div>
        <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-wrap line-clamp-3 font-mono leading-snug">
          {template.body}
        </p>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button
          variant="ghost" size="sm"
          onClick={onEdit}
          className="h-8 text-slate-700"
          data-testid={`template-edit-${template.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost" size="sm"
          onClick={onDelete}
          className="h-8 text-red-600 hover:bg-red-50"
          data-testid={`template-delete-${template.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function TemplateEditor({ template, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || "");
  const [channel, setChannel] = useState(template?.channel || "ig");
  const [body, setBody] = useState(template?.body || "");
  const [promoCode, setPromoCode] = useState(template?.promo_code || "");
  const [saving, setSaving] = useState(false);
  const isEdit = !!template;
  const bodyRef = React.useRef(null);

  const insertVar = (v) => {
    const ta = bodyRef.current;
    if (!ta) {
      setBody((b) => b + v);
      return;
    }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const next = body.slice(0, start) + v + body.slice(end);
    setBody(next);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + v.length;
    }, 10);
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        channel,
        body,
        promo_code: promoCode.trim(),
      };
      if (isEdit) {
        await api.patch(`/templates/${template.id}`, payload);
      } else {
        await api.post("/templates", payload);
      }
      toast.success(isEdit ? "Template saved" : "Template created");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit template" : "New template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
            <div>
              <Label htmlFor="tpl-name">Name</Label>
              <Input
                id="tpl-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cold IG intro"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label htmlFor="tpl-channel">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger id="tpl-channel" data-testid="template-channel-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="tpl-promo">Promo code (optional)</Label>
            <Input
              id="tpl-promo"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="e.g. FIRSTINSPECT50"
              className="font-mono"
              data-testid="template-promo-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Injected wherever you write <code className="bg-slate-100 px-1 rounded">{`{{promo_code}}`}</code> in the body.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="tpl-body">Message body</Label>
              <span className="text-[11px] text-slate-500">{body.length} chars</span>
            </div>
            <Textarea
              id="tpl-body"
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="font-mono text-sm"
              placeholder="Hey {{realtor.first_name}}, …"
              data-testid="template-body-input"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
              Insert variable
            </div>
            <div className="flex flex-wrap gap-1.5">
              {VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVar(v.key)}
                  className="text-[11px] px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 hover:border-slate-400 font-mono inline-flex items-center gap-1 transition-colors"
                  title={v.hint}
                  data-testid={`var-${v.key.replace(/[^a-z]/gi, "_")}`}
                >
                  <Variable className="h-3 w-3 text-slate-400" />
                  {v.key}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="template-cancel">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="template-save">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
